import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Pencil, Save, RotateCcw, X, Plus, Trash2, ArrowRight, Mail, Phone, MapPin, Check,
  Droplet, Leaf, Sparkles, ChevronDown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const SLUG = "botanical-oxygen-care";

// Design tokens
const P = {
  cream: "#FBFAF6",
  cream2: "#F5F1E8",
  sage: "#8DA192",
  sageDark: "#6B7F71",
  sageDeep: "#3E4E44",
  gold: "#C9A961",
  goldSoft: "#D4B678",
  silver: "#C0C7CB",
  ink: "#2A2A2A",
  softInk: "#5A5A5A",
  line: "#E8E4D8",
};

const serif = { fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" };
const sans = { fontFamily: "'Inter', -apple-system, sans-serif" };

// ==== FIBONACCI GOLDEN SPIRAL (SVG decorativo) ====
const FibonacciSpiral = ({ color = P.gold, opacity = 0.3, size = 400, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 200 200" className={className} style={{ opacity }} xmlns="http://www.w3.org/2000/svg">
    {/* rettangoli Fibonacci */}
    <g fill="none" stroke={color} strokeWidth="0.4">
      <rect x="90" y="86" width="8" height="8" />
      <rect x="82" y="86" width="8" height="8" />
      <rect x="82" y="86" width="16" height="16" />
      <rect x="82" y="70" width="16" height="16" />
      <rect x="50" y="70" width="32" height="32" />
      <rect x="50" y="70" width="32" height="32" />
      <rect x="50" y="18" width="52" height="84" />
      <rect x="18" y="18" width="84" height="136" />
    </g>
    {/* spirale aurea */}
    <path
      d="M 94 90 Q 94 86 98 86 Q 90 86 82 86 Q 82 94 90 94 Q 82 102 82 86 Q 98 86 98 102 Q 66 102 66 70 Q 118 70 118 122 Q 34 122 34 34 Q 190 34 190 190"
      fill="none"
      stroke={color}
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

// Editable text component
const T = ({ editing, value, onChange, as = "span", className = "", multiline = false, style = {}, placeholder = "" }) => {
  if (!editing) {
    const Tag = as;
    return <Tag className={className} style={style} dangerouslySetInnerHTML={{ __html: (value || "").replace(/\n/g, "<br/>") }} />;
  }
  if (multiline) {
    return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${className} bg-white/95 text-slate-900 border-2 border-amber-500`} style={style} rows={3} placeholder={placeholder} />;
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${className} bg-white/95 text-slate-900 border-2 border-amber-500`} style={style} placeholder={placeholder} />;
};

const BotanicalOxygenCarePage = () => {
  const { user, token } = useAuth();
  const isAdmin = user && ["admin", "direzione"].includes(user.role);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/landing/${SLUG}`).then((r) => setData(r.data)).catch(() => toast.error("Errore caricamento"));
  }, []);

  const set = (path, val) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (k.includes("[")) {
          const [name, idx] = k.split(/[\[\]]/).filter(Boolean);
          ref = ref[name][idx];
        } else {
          ref = ref[k];
        }
      }
      const last = keys[keys.length - 1];
      if (last.includes("[")) {
        const [name, idx] = last.split(/[\[\]]/).filter(Boolean);
        ref[name][idx] = val;
      } else {
        ref[last] = val;
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/landing/${SLUG}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Salvato"); setEditing(false);
    } catch { toast.error("Errore salvataggio"); } finally { setSaving(false); }
  };

  const resetDefault = async () => {
    if (!window.confirm("Ripristinare i contenuti di default?")) return;
    try {
      const r = await axios.post(`${API}/landing/${SLUG}/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data); toast.success("Ripristinato"); setEditing(false);
    } catch { toast.error("Errore"); }
  };

  const uploadImage = async (fieldPath) => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = "image/*";
    inp.onchange = async (e) => {
      const f = e.target.files?.[0]; if (!f) return;
      const img = new Image(); const reader = new FileReader();
      reader.onload = (ev) => { img.src = ev.target.result; };
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const max = 1800;
        let { width, height } = img;
        if (Math.max(width, height) > max) {
          if (width >= height) { height = Math.round(height * (max / width)); width = max; }
          else { width = Math.round(width * (max / height)); height = max; }
        }
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        set(fieldPath, canvas.toDataURL("image/jpeg", 0.85));
        toast.success("Immagine caricata");
      };
      reader.readAsDataURL(f);
    };
    inp.click();
  };

  const copyLink = () => {
    const url = window.location.origin + "/botanical-oxygen-care";
    navigator.clipboard.writeText(url).then(() => toast.success("Link copiato"));
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: P.cream, ...sans }}>Caricamento…</div>;

  const b = data.brand || {};
  const c = data.cover || {};
  const vi = data.visione || { paragraphs: [] };
  const io = data.ioxina || { punti: [] };
  const fi = data.filosofia || { categorie: [] };
  const pi = data.prodotti_intro || {};
  const prods = data.prodotti || [];
  const fo = data.formula || { livelli: [] };
  const na = data.naturalita || { punti: [] };
  const co = data.contatti || {};
  const ft = data.footer || {};

  return (
    <div style={{ backgroundColor: P.cream, color: P.ink, ...sans }} data-testid="botanical-page">
      {/* ADMIN TOOLBAR */}
      {isAdmin && (
        <div className="sticky top-0 z-[70] px-4 py-2 flex items-center justify-between flex-wrap gap-2" style={{ backgroundColor: P.sageDeep, color: P.cream, borderBottom: `2px solid ${P.gold}` }} data-testid="admin-toolbar">
          <div className="flex items-center gap-2 text-xs">
            <span style={{ color: P.gold }} className="font-semibold tracking-wider uppercase">Admin</span>
            <span className="opacity-70">Botanical Oxygen Care</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={copyLink} className="text-slate-900">Copia link</Button>
            {!editing ? (
              <Button size="sm" onClick={() => setEditing(true)} style={{ backgroundColor: P.gold, color: P.sageDeep }} data-testid="btn-edit"><Pencil className="w-4 h-4 mr-1" /> Modifica</Button>
            ) : (
              <>
                <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="btn-save"><Save className="w-4 h-4 mr-1" /> {saving ? "Salvo…" : "Salva"}</Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="text-slate-900"><X className="w-4 h-4 mr-1" /> Annulla</Button>
                <Button size="sm" variant="outline" onClick={resetDefault} className="text-red-600"><RotateCcw className="w-4 h-4 mr-1" /> Reset</Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ==== COVER ==== */}
      <section id="top" className="relative min-h-screen w-full overflow-hidden" data-testid="cover">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${c.image})` }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${P.cream}dd 0%, ${P.cream}88 45%, ${P.cream}f5 100%)` }} />
        {editing && <button onClick={() => uploadImage("cover.image")} className="absolute top-4 right-4 z-30 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: P.gold, color: P.sageDeep }}>Cambia hero</button>}

        {/* Spirale Fibonacci decorativa */}
        <FibonacciSpiral color={P.gold} opacity={0.15} size={720} className="absolute -bottom-40 -right-40" />
        <FibonacciSpiral color={P.sage} opacity={0.1} size={500} className="absolute -top-32 -left-32 rotate-180" />

        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center text-center px-6 md:px-14 py-16">
          <T editing={editing} value={c.eyebrow} onChange={(v) => set("cover.eyebrow", v)} as="p"
             className="text-xs tracking-[8px] uppercase mb-10 block" style={{ color: P.sageDark, ...sans }} />
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-light leading-[0.95] mb-8" style={{ color: P.sageDeep, ...serif }}>
            <T editing={editing} value={c.title_line1} onChange={(v) => set("cover.title_line1", v)} as="span" />
            <br />
            <em style={{ color: P.gold, fontStyle: "italic", fontWeight: 400 }}>
              <T editing={editing} value={c.title_line2} onChange={(v) => set("cover.title_line2", v)} as="span" />
            </em>
          </h1>
          <div className="w-24 h-[2px] mx-auto mb-8" style={{ backgroundColor: P.gold }} />
          <T editing={editing} value={c.subtitle} onChange={(v) => set("cover.subtitle", v)} multiline as="p"
             className="text-lg md:text-2xl font-light max-w-3xl mx-auto mb-10 block" style={{ color: P.softInk, ...sans, fontStyle: "italic" }} />
          <div className="bg-white/80 backdrop-blur-md border rounded-sm px-6 py-4 max-w-2xl mx-auto mb-12" style={{ borderColor: P.gold }}>
            <p className="text-xs tracking-[3px] uppercase mb-2" style={{ color: P.gold, ...sans }}>Il Filo Conduttore</p>
            <T editing={editing} value={c.filo_conduttore} onChange={(v) => set("cover.filo_conduttore", v)} multiline as="p"
               className="text-sm md:text-base italic block" style={{ color: P.sageDeep, ...serif }} />
          </div>
          <a href={c.cta_href} className="inline-flex items-center gap-3 px-10 py-4 text-sm tracking-[3px] uppercase font-semibold transition-all hover:scale-[1.02] shadow-lg"
             style={{ backgroundColor: P.sageDeep, color: P.cream, ...sans }} data-testid="cover-cta">
            <T editing={editing} value={c.cta_label} onChange={(v) => set("cover.cta_label", v)} as="span" /> <ArrowRight className="w-4 h-4" />
          </a>
          <ChevronDown className="w-6 h-6 mt-16 animate-bounce" style={{ color: P.sageDark }} />
        </div>
      </section>

      {/* ==== VISIONE ==== */}
      <section id="visione" className="py-32 px-6 md:px-14 lg:px-24 relative" data-testid="visione">
        <FibonacciSpiral color={P.gold} opacity={0.08} size={400} className="absolute top-10 right-10 pointer-events-none" />
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center relative">
          <div>
            <T editing={editing} value={vi.eyebrow} onChange={(v) => set("visione.eyebrow", v)} as="p"
               className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: P.gold, ...sans }} />
            <h2 className="text-5xl md:text-7xl font-light leading-[1.05] mb-8" style={{ color: P.sageDeep, ...serif }}>
              <T editing={editing} value={vi.title_line1} onChange={(v) => set("visione.title_line1", v)} as="span" />
              <br />
              <em style={{ color: P.gold, fontStyle: "italic" }}>
                <T editing={editing} value={vi.title_line2} onChange={(v) => set("visione.title_line2", v)} as="span" />
              </em>
            </h2>
            <div className="w-16 h-[2px] mb-8" style={{ backgroundColor: P.gold }} />
            {(vi.paragraphs || []).map((p, i) => (
              <div key={i} className="mb-5">
                <T editing={editing} value={p} onChange={(v) => { const arr = [...vi.paragraphs]; arr[i] = v; set("visione.paragraphs", arr); }} multiline as="p"
                   className="text-lg leading-[1.9] font-light block" style={{ color: P.softInk, ...sans }} />
              </div>
            ))}
          </div>
          <div className="relative">
            <img src={vi.image} alt="Visione" className="w-full h-[620px] object-cover shadow-2xl" />
            {editing && <button onClick={() => uploadImage("visione.image")} className="absolute top-3 right-3 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: P.gold, color: P.sageDeep }}>Cambia immagine</button>}
            <div className="absolute -bottom-6 -left-6 h-24 w-24" style={{ backgroundColor: P.gold }} />
          </div>
        </div>
      </section>

      {/* ==== IOXINA ==== */}
      <section id="ioxina" className="py-32 px-6 md:px-14 lg:px-24 relative overflow-hidden" style={{ backgroundColor: P.sageDeep, color: P.cream }} data-testid="ioxina">
        <FibonacciSpiral color={P.gold} opacity={0.15} size={800} className="absolute -top-40 -right-40 pointer-events-none" />
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center relative">
          <div className="relative order-2 md:order-1">
            <img src={io.image} alt="Ioxina" className="w-full h-[560px] object-cover shadow-2xl" />
            {editing && <button onClick={() => uploadImage("ioxina.image")} className="absolute top-3 right-3 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: P.gold, color: P.sageDeep }}>Cambia immagine</button>}
          </div>
          <div className="order-1 md:order-2">
            <T editing={editing} value={io.eyebrow} onChange={(v) => set("ioxina.eyebrow", v)} as="p"
               className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: P.gold, ...sans }} />
            <h2 className="text-5xl md:text-7xl font-light leading-[1.05] mb-8" style={serif}>
              <T editing={editing} value={io.title_line1} onChange={(v) => set("ioxina.title_line1", v)} as="span" />
              <br />
              <em style={{ color: P.goldSoft, fontStyle: "italic" }}>
                <T editing={editing} value={io.title_line2} onChange={(v) => set("ioxina.title_line2", v)} as="span" />
              </em>
            </h2>
            <T editing={editing} value={io.descrizione} onChange={(v) => set("ioxina.descrizione", v)} multiline as="p"
               className="text-lg leading-[1.9] font-light mb-6 opacity-90 block" style={sans} />
            <div className="border-l-2 pl-5 py-2 my-6" style={{ borderColor: P.gold }}>
              <T editing={editing} value={io.filo_conduttore} onChange={(v) => set("ioxina.filo_conduttore", v)} multiline as="p"
                 className="text-base italic block" style={{ color: P.goldSoft, ...serif }} />
            </div>
            <div className="space-y-4 mt-8">
              {(io.punti || []).map((p, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <Droplet className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: P.gold }} />
                  <div className="flex-1">
                    {editing ? (
                      <>
                        <Input value={p.titolo} onChange={(e) => { const arr = [...io.punti]; arr[i] = { ...p, titolo: e.target.value }; set("ioxina.punti", arr); }} className="bg-white/95 text-slate-900 mb-1 h-8" />
                        <Textarea value={p.desc} onChange={(e) => { const arr = [...io.punti]; arr[i] = { ...p, desc: e.target.value }; set("ioxina.punti", arr); }} rows={2} className="bg-white/95 text-slate-900 text-sm" />
                      </>
                    ) : (
                      <>
                        <h4 className="text-base font-semibold" style={serif}>{p.titolo}</h4>
                        <p className="text-sm opacity-80" style={sans}>{p.desc}</p>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==== FILOSOFIA ==== */}
      <section id="filosofia" className="py-32 px-6 md:px-14 lg:px-24 relative" data-testid="filosofia">
        <div className="max-w-6xl mx-auto text-center mb-16">
          <T editing={editing} value={fi.eyebrow} onChange={(v) => set("filosofia.eyebrow", v)} as="p"
             className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: P.gold, ...sans }} />
          <h2 className="text-5xl md:text-7xl font-light leading-[1.05] mb-6" style={{ color: P.sageDeep, ...serif }}>
            <T editing={editing} value={fi.title_line1} onChange={(v) => set("filosofia.title_line1", v)} as="span" />
            <br />
            <em style={{ color: P.gold, fontStyle: "italic" }}>
              <T editing={editing} value={fi.title_line2} onChange={(v) => set("filosofia.title_line2", v)} as="span" />
            </em>
          </h2>
          <T editing={editing} value={fi.descrizione} onChange={(v) => set("filosofia.descrizione", v)} multiline as="p"
             className="text-lg leading-[1.9] font-light max-w-3xl mx-auto block" style={{ color: P.softInk, ...sans }} />
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-6 mb-16">
          {(fi.categorie || []).map((cat, i) => (
            <div key={i} className="bg-white p-6 text-center border shadow-sm hover:shadow-lg transition-all relative group" style={{ borderColor: P.line }} data-testid={`categoria-${i}`}>
              {editing ? (
                <>
                  <Input value={cat.emoji} onChange={(e) => { const arr = [...fi.categorie]; arr[i] = { ...cat, emoji: e.target.value }; set("filosofia.categorie", arr); }} className="text-3xl text-center mb-2" />
                  <Input value={cat.label} onChange={(e) => { const arr = [...fi.categorie]; arr[i] = { ...cat, label: e.target.value }; set("filosofia.categorie", arr); }} className="text-sm text-center" />
                </>
              ) : (
                <>
                  <div className="text-4xl mb-3">{cat.emoji}</div>
                  <p className="text-sm font-medium tracking-wide" style={{ color: P.sageDeep, ...sans }}>{cat.label}</p>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="max-w-6xl mx-auto relative">
          <img src={fi.image} alt="Filosofia" className="w-full h-[500px] object-cover shadow-2xl" />
          {editing && <button onClick={() => uploadImage("filosofia.image")} className="absolute top-3 right-3 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: P.gold, color: P.sageDeep }}>Cambia immagine</button>}
        </div>
      </section>

      {/* ==== PRODOTTI - Intro ==== */}
      <section id="prodotti" className="py-24 px-6 md:px-14 lg:px-24 text-center relative" style={{ backgroundColor: P.cream2 }} data-testid="prodotti-intro">
        <FibonacciSpiral color={P.sage} opacity={0.1} size={500} className="absolute -top-20 -left-20 pointer-events-none" />
        <div className="max-w-4xl mx-auto relative">
          <T editing={editing} value={pi.eyebrow} onChange={(v) => set("prodotti_intro.eyebrow", v)} as="p"
             className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: P.gold, ...sans }} />
          <T editing={editing} value={pi.title} onChange={(v) => set("prodotti_intro.title", v)} as="h2"
             className="text-5xl md:text-6xl font-light leading-[1.05] mb-6 block" style={{ color: P.sageDeep, ...serif }} />
          <div className="w-16 h-[2px] mx-auto mb-6" style={{ backgroundColor: P.gold }} />
          <T editing={editing} value={pi.subtitle} onChange={(v) => set("prodotti_intro.subtitle", v)} multiline as="p"
             className="text-lg font-light block" style={{ color: P.softInk, ...sans }} />
        </div>
      </section>

      {/* ==== SCHEDE PRODOTTI ==== */}
      <section className="py-16 px-6 md:px-14 lg:px-24" style={{ backgroundColor: P.cream2 }}>
        <div className="max-w-6xl mx-auto space-y-20">
          {(prods || []).map((p, i) => {
            const rev = i % 2 === 1;
            return (
              <div key={i} className={`grid md:grid-cols-2 gap-12 items-center ${rev ? "md:grid-flow-dense" : ""}`} data-testid={`prodotto-${i}`}>
                <div className={`relative ${rev ? "md:col-start-2" : ""}`}>
                  <img src={p.image} alt={p.nome} className="w-full h-[560px] object-cover shadow-2xl" />
                  {editing && <button onClick={() => uploadImage(`prodotti[${i}].image`)} className="absolute top-3 right-3 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: P.gold, color: P.sageDeep }}>Cambia immagine</button>}
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur px-4 py-2 shadow-md">
                    <span className="text-xs tracking-[3px] uppercase" style={{ color: P.gold, ...sans }}>Prodotto</span>
                    <div className="text-2xl font-light" style={{ color: P.sageDeep, ...serif }}>
                      {editing ? <Input value={p.codice} onChange={(e) => set(`prodotti[${i}].codice`, e.target.value)} className="w-16 h-8 text-lg" /> : p.codice}
                    </div>
                  </div>
                </div>
                <div className={rev ? "md:col-start-1" : ""}>
                  <T editing={editing} value={p.nome} onChange={(v) => set(`prodotti[${i}].nome`, v)} as="h3"
                     className="text-3xl md:text-4xl font-light tracking-tight leading-[1.1] mb-2 block" style={{ color: P.sageDeep, ...serif }} />
                  <T editing={editing} value={p.sottotitolo} onChange={(v) => set(`prodotti[${i}].sottotitolo`, v)} as="p"
                     className="text-xs tracking-[3px] uppercase mb-6 block" style={{ color: P.gold, ...sans }} />
                  <T editing={editing} value={p.obiettivo} onChange={(v) => set(`prodotti[${i}].obiettivo`, v)} multiline as="p"
                     className="text-lg italic leading-relaxed mb-6 block" style={{ color: P.softInk, ...serif }} />

                  <div className="border-t border-b py-4 mb-5" style={{ borderColor: P.line }}>
                    <p className="text-xs tracking-[3px] uppercase mb-3" style={{ color: P.gold, ...sans }}>Ingredienti principali</p>
                    <div className="flex flex-wrap gap-2">
                      {(p.ingredienti || []).map((ing, ii) => (
                        editing ? (
                          <div key={ii} className="inline-flex items-center gap-1 bg-white border rounded-full px-2" style={{ borderColor: P.gold }}>
                            <Input value={ing} onChange={(e) => { const arr = [...p.ingredienti]; arr[ii] = e.target.value; set(`prodotti[${i}].ingredienti`, arr); }} className="border-0 h-6 text-xs w-28" />
                            <button onClick={() => { const arr = p.ingredienti.filter((_, k) => k !== ii); set(`prodotti[${i}].ingredienti`, arr); }} className="text-red-500"><X className="w-3 h-3" /></button>
                          </div>
                        ) : (
                          <span key={ii} className="inline-block text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: P.cream, color: P.sageDeep, border: `1px solid ${P.line}`, ...sans }}>{ing}</span>
                        )
                      ))}
                      {editing && <button onClick={() => set(`prodotti[${i}].ingredienti`, [...(p.ingredienti || []), "Nuovo"])} className="text-xs" style={{ color: P.gold }}>+</button>}
                    </div>
                  </div>

                  <div className="border-l-2 pl-4 py-1" style={{ borderColor: P.gold }}>
                    <T editing={editing} value={p.filo} onChange={(v) => set(`prodotti[${i}].filo`, v)} multiline as="p"
                       className="text-sm italic block" style={{ color: P.sageDark, ...serif }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ==== FORMULA FIBONACCI ==== */}
      <section id="formula" className="py-32 px-6 md:px-14 lg:px-24 relative overflow-hidden" style={{ backgroundColor: P.sageDeep, color: P.cream }} data-testid="formula">
        <FibonacciSpiral color={P.gold} opacity={0.2} size={900} className="absolute -bottom-60 -right-60 pointer-events-none" />
        <div className="max-w-6xl mx-auto text-center mb-16 relative">
          <T editing={editing} value={fo.eyebrow} onChange={(v) => set("formula.eyebrow", v)} as="p"
             className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: P.gold, ...sans }} />
          <h2 className="text-5xl md:text-7xl font-light leading-[1.05] mb-6" style={serif}>
            <T editing={editing} value={fo.title_line1} onChange={(v) => set("formula.title_line1", v)} as="span" />
            <br />
            <em style={{ color: P.goldSoft, fontStyle: "italic" }}>
              <T editing={editing} value={fo.title_line2} onChange={(v) => set("formula.title_line2", v)} as="span" />
            </em>
          </h2>
          <div className="w-16 h-[2px] mx-auto mb-6" style={{ backgroundColor: P.gold }} />
          <T editing={editing} value={fo.descrizione} onChange={(v) => set("formula.descrizione", v)} multiline as="p"
             className="text-lg opacity-90 font-light max-w-3xl mx-auto block" style={sans} />
        </div>

        {/* Grafica cerchi concentrici + tabella percentuali */}
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center relative">
          {/* Cerchi concentrici Fibonacci */}
          <div className="relative aspect-square max-w-lg mx-auto">
            {(fo.livelli || []).map((lv, i, arr) => {
              // grandezza cerchio proporzionale alla %
              const size = Math.max(15, lv.pct);
              const opacity = 0.5 + (i / arr.length) * 0.5;
              return (
                <div key={i} className="absolute rounded-full border-2 flex items-center justify-center"
                  style={{
                    width: `${size}%`, height: `${size}%`,
                    top: "50%", left: "50%",
                    transform: "translate(-50%, -50%)",
                    borderColor: lv.colore || P.gold,
                    backgroundColor: `${lv.colore || P.gold}${i === 0 ? "40" : "20"}`,
                    opacity,
                  }}
                />
              );
            })}
            {/* Numero centrale */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="text-6xl md:text-7xl font-light" style={{ color: P.gold, ...serif }}>φ</div>
                <div className="text-xs tracking-[4px] uppercase mt-2 opacity-70" style={sans}>Fibonacci</div>
              </div>
            </div>
          </div>

          {/* Tabella percentuali */}
          <div className="space-y-3">
            {(fo.livelli || []).map((lv, i) => (
              <div key={i} className="flex items-center gap-4 py-2 border-b" style={{ borderColor: `${P.gold}30` }}>
                <div className="text-3xl md:text-4xl font-light w-20 text-right" style={{ color: lv.colore || P.gold, ...serif }}>
                  {editing ? <Input type="number" value={lv.pct} onChange={(e) => { const arr = [...fo.livelli]; arr[i] = { ...lv, pct: parseInt(e.target.value) || 0 }; set("formula.livelli", arr); }} className="bg-white/95 text-slate-900 h-9 text-lg text-right" /> : `${lv.pct}%`}
                </div>
                <div className="flex-1">
                  {editing ? <Input value={lv.componente} onChange={(e) => { const arr = [...fo.livelli]; arr[i] = { ...lv, componente: e.target.value }; set("formula.livelli", arr); }} className="bg-white/95 text-slate-900" /> : <span className="text-base" style={sans}>{lv.componente}</span>}
                </div>
                <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: lv.colore || P.gold }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==== NATURALITÀ ==== */}
      <section id="naturalita" className="py-32 px-6 md:px-14 lg:px-24 relative" data-testid="naturalita">
        <div className="max-w-4xl mx-auto text-center mb-14">
          <T editing={editing} value={na.eyebrow} onChange={(v) => set("naturalita.eyebrow", v)} as="p"
             className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: P.gold, ...sans }} />
          <h2 className="text-5xl md:text-6xl font-light leading-[1.05] mb-6" style={{ color: P.sageDeep, ...serif }}>
            <T editing={editing} value={na.title_line1} onChange={(v) => set("naturalita.title_line1", v)} as="span" />
            <br />
            <em style={{ color: P.gold, fontStyle: "italic" }}>
              <T editing={editing} value={na.title_line2} onChange={(v) => set("naturalita.title_line2", v)} as="span" />
            </em>
          </h2>
          <T editing={editing} value={na.subtitle} onChange={(v) => set("naturalita.subtitle", v)} as="p"
             className="text-lg font-light block" style={{ color: P.softInk, ...sans }} />
        </div>
        <div className="max-w-4xl mx-auto grid sm:grid-cols-2 gap-3">
          {(na.punti || []).map((v, i) => (
            <div key={i} className="flex items-center gap-3 bg-white p-4 border shadow-sm" style={{ borderColor: P.line }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${P.gold}25` }}>
                <Check className="w-5 h-5" style={{ color: P.gold }} strokeWidth={2.5} />
              </div>
              {editing ? (
                <div className="flex-1 flex gap-2">
                  <Input value={v} onChange={(e) => { const arr = [...na.punti]; arr[i] = e.target.value; set("naturalita.punti", arr); }} className="text-sm" />
                  <button onClick={() => { const arr = na.punti.filter((_, k) => k !== i); set("naturalita.punti", arr); }} className="text-red-500"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <span className="text-sm md:text-base" style={{ color: P.ink, ...sans }}>{v}</span>
              )}
            </div>
          ))}
          {editing && <button onClick={() => set("naturalita.punti", [...(na.punti || []), "Nuovo punto"])} className="text-xs col-span-full" style={{ color: P.gold }}><Plus className="w-4 h-4 inline" /> Aggiungi</button>}
        </div>
      </section>

      {/* ==== CONTATTI ==== */}
      <section id="contatti" className="py-32 px-6 md:px-14 lg:px-24 relative overflow-hidden" style={{ backgroundColor: P.sageDeep, color: P.cream }} data-testid="contatti">
        <FibonacciSpiral color={P.gold} opacity={0.15} size={700} className="absolute -top-40 -left-40 pointer-events-none" />
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-16 relative">
          <div>
            <T editing={editing} value={co.eyebrow} onChange={(v) => set("contatti.eyebrow", v)} as="p"
               className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: P.gold, ...sans }} />
            <h2 className="text-4xl md:text-5xl font-light leading-[1.05] mb-6" style={serif}>
              <T editing={editing} value={co.title_line1} onChange={(v) => set("contatti.title_line1", v)} as="span" />
              <br />
              <em style={{ color: P.goldSoft, fontStyle: "italic" }}>
                <T editing={editing} value={co.title_line2} onChange={(v) => set("contatti.title_line2", v)} as="span" />
              </em>
            </h2>
            <T editing={editing} value={co.subtitle} onChange={(v) => set("contatti.subtitle", v)} multiline as="p"
               className="text-base opacity-85 mb-10 font-light block" style={sans} />
            <div className="space-y-4">
              <div className="flex items-center gap-3" style={sans}>
                <Mail className="w-5 h-5" style={{ color: P.gold }} />
                {editing ? <Input value={co.email} onChange={(e) => set("contatti.email", e.target.value)} className="bg-white/95 text-slate-900" /> : <a href={`mailto:${co.email}`} className="hover:underline">{co.email}</a>}
              </div>
              <div className="flex items-center gap-3" style={sans}>
                <Phone className="w-5 h-5" style={{ color: P.gold }} />
                {editing ? <Input value={co.phone} onChange={(e) => set("contatti.phone", e.target.value)} className="bg-white/95 text-slate-900" /> : <span>{co.phone}</span>}
              </div>
              <div className="flex items-center gap-3" style={sans}>
                <MapPin className="w-5 h-5" style={{ color: P.gold }} />
                {editing ? <Input value={co.sede} onChange={(e) => set("contatti.sede", e.target.value)} className="bg-white/95 text-slate-900" /> : <span>{co.sede}</span>}
              </div>
            </div>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); toast.success("Grazie! Ti contatteremo a breve."); }} className="space-y-4 bg-white/5 backdrop-blur p-6 border" style={{ borderColor: `${P.gold}40` }} data-testid="contact-form">
            <Input placeholder="Nome / Azienda" required className="bg-white/95 text-slate-900 h-12" style={sans} />
            <Input type="email" placeholder="Email" required className="bg-white/95 text-slate-900 h-12" style={sans} />
            <Input placeholder="Ruolo / Attività (farmacia, dermatologo, distributore)" className="bg-white/95 text-slate-900 h-12" style={sans} />
            <Textarea placeholder="Il tuo messaggio" rows={4} required className="bg-white/95 text-slate-900" style={sans} />
            <button type="submit" className="w-full py-4 text-sm tracking-[3px] uppercase font-semibold transition-all hover:scale-[1.01]" style={{ backgroundColor: P.gold, color: P.sageDeep, ...sans }}>
              <T editing={editing} value={co.cta_label} onChange={(v) => set("contatti.cta_label", v)} as="span" />
            </button>
          </form>
        </div>
      </section>

      {/* ==== FOOTER ==== */}
      <footer className="py-10 px-6 md:px-14 text-center" style={{ backgroundColor: P.ink, color: P.cream }}>
        <div className="max-w-6xl mx-auto text-xs opacity-80" style={sans}>
          <T editing={editing} value={ft.copyright} onChange={(v) => set("footer.copyright", v)} as="p" className="mb-2 block" />
          <T editing={editing} value={ft.note} onChange={(v) => set("footer.note", v)} as="p" className="italic opacity-60 block" />
        </div>
      </footer>
    </div>
  );
};

export default BotanicalOxygenCarePage;
